import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { GuideContext, type GuideState } from './guideContext';
import type { GuideAnchor, GuideRegistration } from './types';

/**
 * Holds the Guide's open/closed state and whichever page has registered itself.
 *
 * A page registers imperatively rather than rendering the panel itself, so the
 * panel is mounted exactly once in RootLayout and pages stay unaware of it
 * beyond one hook call.
 */
export function GuideProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [registration, setRegistration] = useState<GuideRegistration | null>(null);
  const [spotlight, setSpotlight] = useState<GuideAnchor | null>(null);
  const location = useLocation();

  // A registration belongs to one page. Navigating drops it (and any
  // highlight) rather than letting a stale one describe the new screen.
  //
  // The previous-path guard is load-bearing, not defensive: child effects run
  // BEFORE the parent's, so on first mount the page has already registered by
  // the time this runs, and an unguarded reset wipes it immediately — the panel
  // then never appears at all.
  const lastPath = useRef(location.pathname);
  useEffect(() => {
    if (lastPath.current === location.pathname) return;
    lastPath.current = location.pathname;
    setRegistration(null);
    setSpotlight(null);
  }, [location.pathname]);

  const register = useCallback((r: GuideRegistration | null) => setRegistration(r), []);
  const showAnchor = useCallback((anchor: GuideAnchor | null) => setSpotlight(anchor), []);

  const value = useMemo<GuideState>(
    () => ({ open, setOpen, registration, register, spotlight, showAnchor }),
    [open, registration, spotlight, register, showAnchor],
  );

  return <GuideContext.Provider value={value}>{children}</GuideContext.Provider>;
}
